<template>
	<div class="cms-social-share-block" :style="containerStyle">
		<span v-if="labelText" class="cms-social-share-block__label">{{ labelText }}</span>
		<div class="cms-social-share-block__buttons">
			<a
				v-for="(platform, idx) in activePlatforms"
				:key="idx"
				:href="shareUrl(platform)"
				class="cms-social-share-block__btn"
				:class="[
					'cms-social-share-block__btn--' + resolvedStyle,
					'cms-social-share-block__btn--' + size,
					resolvedShowLabels ? 'cms-social-share-block__btn--has-label' : ''
				]"
				:style="btnStyle(platform)"
				target="_blank"
				rel="noopener noreferrer"
				:title="'Share on ' + platform.label"
				@click.prevent="openShare(platform)"
			>
				<i :class="'fa fa-' + platform.icon"></i>
				<span v-if="resolvedShowLabels" class="cms-social-share-block__btn-label">{{ platform.label }}</span>
			</a>
		</div>
	</div>
</template>

<script>
/**
 * SocialShareBlock.vue
 * ────────────────────
 * Renders a row of social media share buttons for the current article/page.
 * Each button opens a platform-specific share dialog with the page URL and title.
 *
 * Props:
 * - platforms:    JSON string of enabled platform IDs (e.g. '["facebook","twitter"]')
 * - iconStyle:    'filled' | 'outline' | 'plain' — button visual style
 * - size:         'small' | 'medium' | 'large'
 * - gap:          CSS gap between buttons
 * - labelText:    Optional heading text above the buttons (e.g. "Share this article:")
 * - showLabels:   Whether to show platform name text inside the button
 * - iconColor:    Override colour for plain style
 *
 * Alignment, spacing, background and visibility are wrapper traits — the
 * root is inline-flex so the wrapper's text-align positions it.
 */

import {coerceBool} from '@/websites/cms/blocks/shared/blockUtils.js';

var ALL_PLATFORMS = [
	{
		id: 'facebook', label: 'Facebook', icon: 'facebook', color: '#1877F2',
		shareUrl: function (url) {
			return 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(url);
		}
	},
	{
		id: 'twitter', label: 'X (Twitter)', icon: 'twitter', color: '#000000',
		shareUrl: function (url, title) {
			return 'https://twitter.com/intent/tweet?url=' + encodeURIComponent(url) + '&text=' + encodeURIComponent(title || '');
		}
	},
	{
		id: 'linkedin', label: 'LinkedIn', icon: 'linkedin', color: '#0A66C2',
		shareUrl: function (url) {
			return 'https://www.linkedin.com/sharing/share-offsite/?url=' + encodeURIComponent(url);
		}
	},
	{
		id: 'pinterest', label: 'Pinterest', icon: 'pinterest', color: '#BD081C',
		shareUrl: function (url, title) {
			return 'https://pinterest.com/pin/create/button/?url=' + encodeURIComponent(url) + '&description=' + encodeURIComponent(title || '');
		}
	},
	{
		id: 'whatsapp', label: 'WhatsApp', icon: 'whatsapp', color: '#25D366',
		shareUrl: function (url, title) {
			return 'https://api.whatsapp.com/send?text=' + encodeURIComponent((title ? title + ' ' : '') + url);
		}
	},
	{
		id: 'reddit', label: 'Reddit', icon: 'reddit-alien', color: '#FF4500',
		shareUrl: function (url, title) {
			return 'https://www.reddit.com/submit?url=' + encodeURIComponent(url) + '&title=' + encodeURIComponent(title || '');
		}
	},
	{
		id: 'email', label: 'Email', icon: 'envelope', color: '#7f8c99',
		shareUrl: function (url, title) {
			return 'mailto:?subject=' + encodeURIComponent(title || url) + '&body=' + encodeURIComponent(url);
		}
	}
];

var DEFAULT_PLATFORM_IDS = ['facebook', 'twitter', 'linkedin', 'email'];

export default {
	name: 'SocialShareBlock',
	inheritAttrs: false,

	props: {
		platforms: {
			type: String,
			default: function () {
				return JSON.stringify(DEFAULT_PLATFORM_IDS);
			}
		},
		iconStyle: {
			type: String,
			default: 'filled',
			validator: function (v) {
				return ['filled', 'outline', 'plain'].indexOf(v) !== -1;
			}
		},
		size: {
			type: String,
			default: 'medium',
			validator: function (v) {
				return ['small', 'medium', 'large'].indexOf(v) !== -1;
			}
		},
		gap: {
			type: String,
			default: '0.5rem'
		},
		labelText: {
			type: String,
			default: 'Share this article:'
		},
		showLabels: {
			type: [Boolean, String],
			default: false
		},
		iconColor: {
			type: String,
			default: ''
		}
	},

	computed: {
		resolvedShowLabels: function () {
			return coerceBool(this.showLabels);
		},
		resolvedStyle: function () {
			return this.iconStyle || 'filled';
		},

		enabledIds: function () {
			try
			{
				var result = typeof this.platforms === 'string' ? JSON.parse(this.platforms) : this.platforms;
				return Array.isArray(result) ? result : DEFAULT_PLATFORM_IDS;
			} catch (e)
			{
				return DEFAULT_PLATFORM_IDS;
			}
		},

		activePlatforms: function () {
			var ids = this.enabledIds;
			return ALL_PLATFORMS.filter(function (p) {
				return ids.indexOf(p.id) !== -1;
			});
		},

		containerStyle: function () {
			return {gap: this.gap};
		}
	},

	methods: {
		shareUrl: function (platform) {
			var url = (typeof window !== 'undefined' && window.location) ? window.location.href : '#';
			var title = (typeof document !== 'undefined' && document.title) ? document.title : '';
			return platform.shareUrl(url, title);
		},

		btnStyle: function (platform) {
			var style = this.resolvedStyle;
			if (style === 'filled')
			{
				return {backgroundColor: platform.color, color: '#FFF'};
			}
			if (style === 'outline')
			{
				return {borderColor: platform.color, color: platform.color};
			}
			// plain
			return {color: this.iconColor || platform.color};
		},

		openShare: function (platform) {
			var url = (typeof window !== 'undefined' && window.location) ? window.location.href : '';
			var title = (typeof document !== 'undefined' && document.title) ? document.title : '';
			var shareLink = platform.shareUrl(url, title);

			// Email links open in the current tab; all others in a popup
			if (platform.id === 'email')
			{
				window.location.href = shareLink;
			}
			else
			{
				window.open(shareLink, '_blank', 'width=600,height=400,noopener,noreferrer');
			}
		}
	}
};
</script>

<style scoped>
.cms-social-share-block {
	display: inline-block;
	max-width: 100%;
	vertical-align: middle;
}

.cms-social-share-block__label {
	display: block;
	font-weight: 600;
	margin-bottom: 0.5rem;
	font-size: 1em;
	color: inherit;
}

.cms-social-share-block__buttons {
	display: inline-flex;
	flex-wrap: wrap;
	gap: inherit;
	align-items: center;
	max-width: 100%;
}

.cms-social-share-block__btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 0.4rem;
	text-decoration: none;
	cursor: pointer;
	border: 2px solid transparent;
	transition: opacity 0.15s, transform 0.15s;
	white-space: nowrap;
}

.cms-social-share-block__btn:hover {
	opacity: 0.8;
	transform: scale(1.05);
}

/* ── Sizes ── */
.cms-social-share-block__btn--small {
	width: 32px;
	height: 32px;
	font-size: 1em;
	border-radius: 50%;
}

.cms-social-share-block__btn--medium {
	width: 40px;
	height: 40px;
	font-size: 1.15em;
	border-radius: 50%;
}

.cms-social-share-block__btn--large {
	width: 52px;
	height: 52px;
	font-size: 1.4em;
	border-radius: 50%;
}

/* When labels are shown, use pill shape and auto width */
.cms-social-share-block__btn--small.cms-social-share-block__btn--has-label {
	width: auto;
	padding: 0.3rem 0.9rem;
	border-radius: 999px;
}

.cms_social_share_block__btn--medium.cms-social-share-block__btn--has-label {
	width: auto;
	padding: 0.45rem 1.1rem;
	border-radius: 999px;
}

.cms_social_share_block__btn--large.cms-social-share-block__btn--has-label {
	width: auto;
	padding: 0.6rem 1.4rem;
	border-radius: 999px;
}

/* ── Styles ── */
.cms-social-share-block__btn--filled {
	border-radius: 50%;
}

.cms-social-share-block__btn--outline {
	background: transparent;
	border-radius: 50%;
}

.cms-social-share-block__btn--plain {
	background: transparent;
	border-color: transparent;
	border-radius: 4px;
}

.cms-social-share-block__btn-label {
	font-size: 0.9em;
	font-weight: 600;
}
</style>
